import { useState } from 'react';
import { FormSchema } from '../../../types/field-builder';

interface FormPreviewProps {
  schema: FormSchema;
}

export default function FormPreview({ schema }: FormPreviewProps) {
  const [activeTab, setActiveTab] = useState(schema.tabs[0]?.id || '');
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const handleFieldChange = (key: string, value: any) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const renderField = (field: any) => {
    const value = formValues[field.key] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.readonly}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.readonly}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.readonly}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            disabled={field.readonly}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            disabled={field.readonly}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Selecione...</option>
            {field.options?.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        return (
          <div className="space-y-2">
            {field.options?.map((option: string) => (
              <label key={option} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v: string) => v !== option);
                    handleFieldChange(field.key, newValues);
                  }}
                  disabled={field.readonly}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'boolean':
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              disabled={field.readonly}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed"
            />
            <span className="text-sm text-gray-700">Sim</span>
          </label>
        );

      default:
        return null;
    }
  };

  if (schema.tabs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Nenhuma aba configurada
        </h3>
        <p className="text-gray-600">
          Adicione abas e campos no editor para visualizar o formulário
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-4">
          {schema.tabs.sort((a, b) => a.order - b.order).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {schema.fields
            .filter(f => f.tab_id === activeTab)
            .sort((a, b) => a.order - b.order)
            .map((field) => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                  {field.readonly && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                      Somente leitura
                    </span>
                  )}
                </label>
                {renderField(field)}
                {field.help_text && (
                  <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                )}
              </div>
            ))}

          {schema.fields.filter(f => f.tab_id === activeTab).length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-5xl text-gray-300 mb-3"></i>
              <p className="text-gray-600">Nenhum campo nesta aba</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Esta é uma pré-visualização do formulário
          </p>
          <button
            type="button"
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 whitespace-nowrap cursor-pointer"
          >
            Salvar (Exemplo)
          </button>
        </div>
      </div>
    </div>
  );
}
